import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import { RowDelete as Delete } from '@carbon/react/icons';
import {
  TableContainer,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableSelectRow,
  TableSelectAll,
  TableToolbar,
  TableToolbarAction,
  TableToolbarContent,
  TableToolbarSearch,
  TableToolbarMenu,
  TableBatchActions,
  TableBatchAction,
  TableExpandRow,
  TableExpandHeader,
  TableExpandedRow,
  DataTableSkeleton,
} from '@carbon/react';
import { useUniqueId } from '../hooks';
import Pagination from './Pagination';
import { TABLE_SIZE, TABLE_SORT_DIRECTION } from '../misc';

import { getNextSort } from '../hooks/useSortInfo';
import useSetExpandRow from '../hooks/useSetExpandRow';


import "./_overrides.scss";

const CustomDataTable = ({
  id,
  columns,
  hasSelection = false,
  pageSize = 5,
  rows: propRows,
  size = TABLE_SIZE.REGULAR,
  sortInfo,
  page,
  zebra,
  totalItems,
  loadItems,
  hasExpandedRow = false,
  expandedRowComponent,
  customRowActionsComponent,
  useTableToolbar = false,
  onClickInsideExpandedRow,
  innerSelections,
  search,
  collator = new Intl.Collator(),
  start = 0,
  isLoading = false,
  hookExpandRow = (id) => {},
}) => {
  const [rows, setRows] = useState(propRows);

  useEffect(() => {
    setRows(propRows);
  }, [propRows]);

  const [setExpandRow] = useSetExpandRow(rows, setRows);

  const elementId = useUniqueId(id);
  const selectedRowsCountInFiltered = rows.filter(({ selected }) => selected)
    .length;
  const selectedAllInFiltered =
    selectedRowsCountInFiltered > 0 &&
    rows.length === selectedRowsCountInFiltered;

  const hasBatchActions = hasSelection && selectedRowsCountInFiltered > 0;
  const { sortId, columnId, direction: sortDirection } = sortInfo;

  const sortColumnId = sortId || columnId;

  const selectionAllName = !hasSelection
    ? undefined
    : `__custom-data-table_select-all_${elementId}`;

  const handleCancelSelection = () => {
    //setRowSelection(undefined, false);
  };

  const handleChangeSearchString = () => {
    //setSearchString(target.value);
  };

  const handleChangeSelection = (_event) => {
    /*const { currentTarget } = event;
    const row = currentTarget.closest('tr');
    if (row) {
      setRowSelection(Number(row.dataset.rowId), currentTarget.checked);
    }*/
  };

  const handleChangeSelectionAll = (_event) => {
    //setRowSelection(undefined, event.currentTarget.checked);
  };

  const makeItBold = (src) => {
    if (search && search.length > 0 && typeof src === 'string') {
      const regex = new RegExp(`(${search})`, 'gi');

      src = src.replace(regex, function (a, b) {
        return '<span style="font-weight: bold">' + b + '</span>';
      });

      src = <div dangerouslySetInnerHTML={{ __html: src }} />;
    }

    return src;
  };

  const currentOrder = useRef();

  const handleChangeSort = (event) => {
    const { currentTarget } = event;
    const {
      columnId,
      sortId,
      sortCycle,
      sortDirection: oldDirection,
    } = currentTarget.dataset;

    currentOrder.current = [
      `${sortId || columnId} ${getNextSort({ sortCycle, oldDirection })}`,
    ];
    console.info("handleChangeSort gets triggered");
    loadItems({
      pageSize,
      pageIndex: 1,
      sortBy: currentOrder.current,
    });
    //setSortInfo({ columnId, sortCycle, oldDirection });
  };

  const handleChangePageSize = ({ pageSize }) => {
    loadItems({ pageSize, pageIndex: page, sortBy: currentOrder.current });
  };

  const handleChangeStart = ({ page }) => {
    console.info("handleChangeStart gets triggered");
    loadItems({ pageSize, pageIndex: page, sortBy: currentOrder.current });
  };

  const handleExpandRow = (event) => {
    if (hasExpandedRow) {
      if (
        (typeof event.target.className === 'string' &&
          !event.target.className.includes('overflow-menu')) ||
        (typeof event.target.className === 'object' &&
          event.target.className.baseVal === 'cds--table-expand__svg')
      ) {
        const { currentTarget } = event;
        const row = currentTarget.closest('tr');
        if (row) {
          hookExpandRow(Number(row.dataset.rowId));
          setExpandRow(Number(row.dataset.rowId));
        }
      }
    }
  };

  const lastSearch = useRef();

  useEffect(() => {
    if (!lastSearch.current && !search) {
      return;
    }
    if (lastSearch.current === search) {
      return;
    }

    lastSearch.current = search;
    loadItems({
      pageSize,
      pageIndex: 1,
      sortBy: currentOrder.current,
      search: search,
    });
  }, [search, loadItems, pageSize]);

  const handleDeleteRows = () => {};

  const TableRowByCondition = hasExpandedRow ? TableExpandRow : TableRow;
  const ExpandedRowComponent = expandedRowComponent;

  const CustomRowActionsComponent = customRowActionsComponent;
  /* eslint-disable no-script-url */
  if (size == "short") {
    size = "xs";
  }

  const tableHeads = columns.map(({ id: columnId, sortId, sortCycle, title }) => {
    const sortDirectionForThisCell =
      sortCycle &&
      ((sortId || columnId) === sortColumnId
        ? sortDirection
        : TABLE_SORT_DIRECTION.NONE);
    return (
      <TableHeader
        key={columnId}
        isSortable={Boolean(sortCycle)}
        isSortHeader={
          sortCycle && (sortId || columnId) === sortColumnId
        }
        sortDirection={sortDirectionForThisCell}
        data-column-id={columnId}
        data-sort-id={sortId || columnId}
        data-sort-cycle={sortCycle}
        data-sort-direction={sortDirectionForThisCell}
        onClick={handleChangeSort}>
        {title}
      </TableHeader>
    );
  });
  return (
    <TableContainer>
      {useTableToolbar && (
        <TableToolbar>
          <TableBatchActions
            shouldShowBatchActions={hasBatchActions}
            totalSelected={selectedRowsCountInFiltered}
            onCancel={handleCancelSelection}>
            <TableBatchAction
              tabIndex={hasBatchActions ? 0 : -1}
              renderIcon={Delete}
              onClick={handleDeleteRows}>
              Delete
            </TableBatchAction>
          </TableBatchActions>
          <TableToolbarContent>
            <TableToolbarSearch
              tabIndex={hasBatchActions ? -1 : 0}
              onChange={handleChangeSearchString}
            />
            <TableToolbarMenu tabIndex={hasBatchActions ? -1 : 0}>
              <TableToolbarAction primaryFocus onClick={() => alert('Alert 1')}>
                Action 1
              </TableToolbarAction>
              <TableToolbarAction onClick={() => alert('Alert 2')}>
                Action 2
              </TableToolbarAction>
              <TableToolbarAction onClick={() => alert('Alert 3')}>
                Action 3
              </TableToolbarAction>
            </TableToolbarMenu>
          </TableToolbarContent>
        </TableToolbar>
      )}
      {isLoading ? <DataTableSkeleton headers={[{header:"", key: "action"}, ...columns.map(({ id, title }) => {return {header: title, key: id}})]}  rowCount={pageSize} showToolbar={false} showHeader={false} size={size} compact/> :
        <Table size={size} isSortable>
          <TableHead>
            <TableRow>
              {hasExpandedRow && <TableExpandHeader />}
              {hasSelection && (
                <TableSelectAll
                  id={`${elementId}--select-all`}
                  checked={selectedAllInFiltered}
                  indeterminate={
                    selectedRowsCountInFiltered > 0 && !selectedAllInFiltered
                  }
                  aria-label="Select all rows"
                  name={selectionAllName}
                  onSelect={handleChangeSelectionAll}
                />
              )}

              {tableHeads}

              {customRowActionsComponent && <TableHeader />}
            </TableRow>
          </TableHead>
          <TableBody zebra={zebra}>

            {rows.map((row) => {
              const { id: rowId, selected } = row;
              const selectionName = !hasSelection
                ? undefined
                : `__custom-data-table_${elementId}_${rowId}`;
              return (
                <React.Fragment key={rowId}>
                  <TableRowByCondition
                    key={rowId}
                    isSelected={hasSelection && selected}
                    data-row-id={rowId}
                    onClick={handleExpandRow}
                    onExpand={handleExpandRow}
                    isExpanded={row.expanded ? row.expanded : false}>
                    {hasSelection && (
                      <TableSelectRow
                        id={`${elementId}--select-${rowId}`}
                        checked={Boolean(selected)}
                        name={selectionName}
                        aria-label="Select row"
                        onSelect={handleChangeSelection}
                      />
                    )}
                    {columns.map(({ id: columnId }) => (
                      <TableCell key={columnId}>
                        {makeItBold(row[columnId])}
                      </TableCell>
                    ))}
                    {customRowActionsComponent && (
                      <TableCell>
                        <CustomRowActionsComponent row={row} />
                      </TableCell>
                    )}
                  </TableRowByCondition>
                  {hasExpandedRow && (
                    <TableExpandedRow colSpan={columns.length + 2}>
                      <ExpandedRowComponent
                        id={rowId}
                        expanded={row.expanded}
                        item={row}
                        isVisible={row.expanded}
                        onClickInsideExpandedRow={onClickInsideExpandedRow}
                        innerSelections={innerSelections}
                      />
                    </TableExpandedRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      }
      {typeof pageSize !== 'undefined' && (
        <Pagination
          start={0}
          page={page}
          count={totalItems}
          pageSize={pageSize}
          pageSizes={[5, 10, 15]}
          totalItems={totalItems}
          onChangePageSize={handleChangePageSize}
          onChangeStart={handleChangeStart}
        />
      )}
    </TableContainer>
  );
  /* eslint-enable no-script-url */
};

CustomDataTable.propTypes = {
  /**
   * The g11n collator to use.
   */
  collator: PropTypes.shape({}),

  /**
   * Data table columns.
   */
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      sortCycle: PropTypes.string,
    })
  ),

  /**
   * `true` if the the table should support selection UI. Corresponds to the attribute with the same name.
   */
  hasSelection: PropTypes.bool,

  /**
   * Provide an `id` to uniquely identify the Checkbox input
   */
  id: PropTypes.string,

  /**
   * Number of items per page.
   */
  pageSize: PropTypes.number,

  /**
   * Data table rows.
   */
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      selected: PropTypes.bool,
    })
  ),

  /**
   * `true` if the the table should use the compact version of the UI. Corresponds to the attribute with the same name.
   */
  size: PropTypes.string,

  /**
   * Table sorting info.
   */
  sortInfo: PropTypes.shape({
    columnId: PropTypes.string,
    direction: PropTypes.string,
  }),

  /**
   * The row number where current page start with, index that starts with zero. Corresponds to the attribute with the same name.
   */
  page: PropTypes.number,

  /**
   * `true` if the zebra stripe should be shown.
   */
  zebra: PropTypes.bool,

  hasExpandedRow: PropTypes.bool,
};

export default CustomDataTable;
